import { useCallback, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { config } from '@app/config';
import { BILLING_PATH, MCP_LOGIN_PATH } from '@app/constants/navigation';
import { useUserPrint } from '@app/hooks/useUserPrint';
import { getAccessToken } from '@app/utils/accessTokenStorage';

import {
  MCP_AUTHORIZATION_CALLBACK_PATH,
  MCP_SESSION_MESSAGE,
} from '../constants';
import {
  isMcpNavigateMessage,
  isMcpOAuthCompleteMessage,
  isMcpReadyMessage,
} from '../utils/bridgeMessages';
import { getMcpFrameUrl, getMcpResourceOrigin } from '../utils/frameUrl';

import { useMcpAccess } from './useMcpAccess';
import { useMcpIntegrationEffects } from './useMcpIntegrationEffects';

import type { McpIntegrationError } from '../types';

/** CRM owns only embedding and navigation. MCP owns UI, API and billing state. */
export const useMcpIntegration = () => {
  const { params } = useUserPrint();
  const { environment, allowed } = useMcpAccess();
  const { UserId, Database, ActiveBranchId } = params;
  const { pathname, search } = useLocation();
  const { draftId } = useParams<{ draftId?: string }>();
  const navigate = useNavigate();
  const [attempt, setAttempt] = useState(0);
  const [readyFrameKey, setReadyFrameKey] = useState<string | null>(null);
  const [timedOutFrameKey, setTimedOutFrameKey] = useState<string | null>(null);

  const origin = useMemo(
    () => getMcpResourceOrigin(config.mcpResourceOrigin),
    []
  );
  const isConsentPage = pathname === MCP_LOGIN_PATH;
  const host = useMemo(
    () => getMcpFrameUrl({ origin, isConsentPage, search, draftId }),
    [origin, isConsentPage, search, draftId]
  );
  const error: McpIntegrationError | null = !allowed
    ? 'access-denied'
    : !origin
    ? 'not-configured'
    : !host
    ? 'invalid-link'
    : null;

  // Смена пользователя, организации, филиала или повтор создают новый фрейм:
  // прежний контекст и его сообщения к новому фрейму не переходят.
  const frameKey = `${environment}:${Database}:${UserId}:${ActiveBranchId}:${host}:${attempt}`;
  const isFrameEnabled = allowed && Boolean(host);
  const isHandshakePending = isFrameEnabled && readyFrameKey !== frameKey;
  const isHandshakeTimedOut =
    isHandshakePending && timedOutFrameKey === frameKey;

  const messageHandler = useCallback(
    (data: unknown, source: Window) => {
      if (!origin || !allowed || !environment) return;

      if (isMcpReadyMessage(data)) {
        setReadyFrameKey(frameKey);
        source.postMessage(
          {
            type: MCP_SESSION_MESSAGE,
            nonce: data.nonce,
            accessToken: getAccessToken() ?? '',
            branchId: ActiveBranchId,
            userId: UserId,
            database: Database,
            environment,
          },
          origin
        );

        return;
      }

      if (isMcpNavigateMessage(data)) {
        navigate(BILLING_PATH);

        return;
      }

      if (isConsentPage && isMcpOAuthCompleteMessage(data)) {
        const callback = new URL(MCP_AUTHORIZATION_CALLBACK_PATH, origin);
        callback.search = new URLSearchParams({
          code: data.code,
          state: data.state,
        }).toString();
        window.location.assign(callback.href);
      }
    },
    [
      origin,
      environment,
      allowed,
      frameKey,
      ActiveBranchId,
      UserId,
      Database,
      navigate,
      isConsentPage,
    ]
  );

  const handshakeTimeoutHandler = useCallback(() => {
    setTimedOutFrameKey(frameKey);
  }, [frameKey]);

  const retryHandler = useCallback(() => {
    setAttempt((value) => value + 1);
  }, []);

  const resetHandshakeHandler = useCallback(() => {
    setReadyFrameKey(null);
    setTimedOutFrameKey(null);
  }, []);

  useMcpIntegrationEffects({
    frameKey,
    isFrameEnabled,
    onResetHandshake: resetHandshakeHandler,
    isHandshakePending,
    onHandshakeTimeout: handshakeTimeoutHandler,
  });

  return {
    host: allowed ? host : '',
    error,
    frameKey,
    isHandshakeTimedOut,
    messageHandler,
    retryHandler,
  };
};
